import React, { useState, useMemo, useCallback } from 'react';
import { base_logo_url } from '../../apis/constants';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
  Avatar
} from '@mui/material';
import {
  LocationOn,
  LocalShipping,
  ExpandMore,
  ExpandLess,
  CalendarToday
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Professional color palette for cards - 20 unique schemes
const cardColorSchemes = [
  // Original 10
  {
    primary: '#2563eb',
    light: '#dbeafe',
    gradientStart: '#3b82f6',
    gradientEnd: '#1d4ed8',
    chipBg: '#2563eb',
    accent: '#1e40af'
  },
  {
    primary: '#0891b2',
    light: '#cffafe',
    gradientStart: '#06b6d4',
    gradientEnd: '#0e7490',
    chipBg: '#0891b2',
    accent: '#155e75'
  },
  {
    primary: '#0d9488',
    light: '#ccfbf1',
    gradientStart: '#14b8a6',
    gradientEnd: '#0f766e',
    chipBg: '#0d9488',
    accent: '#115e59'
  },
  {
    primary: '#7c3aed',
    light: '#ede9fe',
    gradientStart: '#8b5cf6',
    gradientEnd: '#6d28d9',
    chipBg: '#7c3aed',
    accent: '#5b21b6'
  },
  {
    primary: '#b45309',
    light: '#fef3c7',
    gradientStart: '#d97706',
    gradientEnd: '#b45309',
    chipBg: '#b45309',
    accent: '#92400e'
  },
  {
    primary: '#c2410c',
    light: '#ffedd5',
    gradientStart: '#ea580c',
    gradientEnd: '#c2410c',
    chipBg: '#c2410c',
    accent: '#9a3412'
  },
  {
    primary: '#be123c',
    light: '#ffe4e6',
    gradientStart: '#e11d48',
    gradientEnd: '#be123c',
    chipBg: '#be123c',
    accent: '#9f1239'
  },
  {
    primary: '#1e40af',
    light: '#dbeafe',
    gradientStart: '#2563eb',
    gradientEnd: '#1e3a8a',
    chipBg: '#1e40af',
    accent: '#1e3a8a'
  },
  {
    primary: '#065f46',
    light: '#d1fae5',
    gradientStart: '#059669',
    gradientEnd: '#065f46',
    chipBg: '#065f46',
    accent: '#064e3b'
  },
  {
    primary: '#6b21a8',
    light: '#f3e8ff',
    gradientStart: '#9333ea',
    gradientEnd: '#6b21a8',
    chipBg: '#6b21a8',
    accent: '#581c87'
  },

  // 10 New Schemes
  {
    primary: '#0284c7',
    light: '#e0f2fe',
    gradientStart: '#38bdf8',
    gradientEnd: '#0284c7',
    chipBg: '#0284c7',
    accent: '#075985'
  }, // Sky Blue
  {
    primary: '#059669',
    light: '#d1fae5',
    gradientStart: '#10b981',
    gradientEnd: '#059669',
    chipBg: '#059669',
    accent: '#047857'
  }, // Emerald
  {
    primary: '#7e22ce',
    light: '#f3e8ff',
    gradientStart: '#a855f7',
    gradientEnd: '#7e22ce',
    chipBg: '#7e22ce',
    accent: '#6b21a8'
  }, // Purple
  {
    primary: '#b91c1c',
    light: '#fee2e2',
    gradientStart: '#ef4444',
    gradientEnd: '#b91c1c',
    chipBg: '#b91c1c',
    accent: '#991b1b'
  }, // Red
  {
    primary: '#c026d3',
    light: '#fae8ff',
    gradientStart: '#d946ef',
    gradientEnd: '#c026d3',
    chipBg: '#c026d3',
    accent: '#a21caf'
  }, // Fuchsia
  {
    primary: '#a16207',
    light: '#fef9c3',
    gradientStart: '#eab308',
    gradientEnd: '#a16207',
    chipBg: '#a16207',
    accent: '#854d0e'
  }, // Yellow
  {
    primary: '#64748b',
    light: '#f1f5f9',
    gradientStart: '#94a3b8',
    gradientEnd: '#64748b',
    chipBg: '#64748b',
    accent: '#475569'
  }, // Slate
  {
    primary: '#0f172a',
    light: '#f8fafc',
    gradientStart: '#334155',
    gradientEnd: '#0f172a',
    chipBg: '#0f172a',
    accent: '#020617'
  }, // Dark Blue
  {
    primary: '#166534',
    light: '#dcfce7',
    gradientStart: '#22c55e',
    gradientEnd: '#166534',
    chipBg: '#166534',
    accent: '#14532d'
  }, // Forest Green
  {
    primary: '#831843',
    light: '#fdf2f8',
    gradientStart: '#ec4899',
    gradientEnd: '#831843',
    chipBg: '#831843',
    accent: '#701a75'
  } // Pink
];

const formatDate = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Carrier logo mapping

const LocationCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'translateY(-2px)'
  }
}));

const LocationHeader = styled(Box)(({ theme, gradientstart, gradientend }) => ({
  padding: theme.spacing(1.5),
  background: `linear-gradient(135deg, ${gradientstart} 0%, ${gradientend} 100%)`,
  color: 'white',
  borderTopLeftRadius: theme.shape.borderRadius,
  borderTopRightRadius: theme.shape.borderRadius,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5)
}));

const HeaderRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%'
});

const DateRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  fontSize: '0.7rem',
  opacity: 0.9
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.6, 1.5),
  fontSize: '0.8rem'
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: theme.palette.action.selected
  }
}));

const CarrierCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const PalletLocationBreakdown = ({ shipments, startDate, endDate }) => {
  console.log(shipments, 'shipments');
  const [expandedLocations, setExpandedLocations] = useState({});
  const dateRangeLabel =
    startDate && endDate
      ? `${formatDate(startDate)} – ${formatDate(endDate)}`
      : startDate
        ? `From ${formatDate(startDate)}`
        : '';

  const nonTlShipments = useMemo(() => {
    return shipments.filter(s => s.shipmentType !== 'TL');
  }, [shipments]);

  const locationData = useMemo(() => {
    const data = {};

    nonTlShipments.forEach(shipment => {
      const rawLocation = (shipment.location || 'Unknown').trim();
      // If location has a "STATE: full address" format, use the prefix before ":"
      // as the dedup key so "TX" and "TX: Gainesville TX 76240 USA" merge into one card
      const colonIdx = rawLocation.indexOf(':');
      const key =
        colonIdx !== -1
          ? rawLocation.substring(0, colonIdx).trim().toLowerCase()
          : rawLocation.toLowerCase();
      const carrier = shipment.carrierName || 'Unknown';
      const carrierLogo = shipment.carrierLogo || '';
      const pallets = Number(shipment.totalUnits) || 0;
      const grossWeight = Number(shipment.totalGrossWeight) || 0;

      if (!data[key]) {
        data[key] = {
          location: rawLocation,
          totalPallets: 0,
          totalGrossWeight: 0,
          carriers: {}
        };
      } else if (rawLocation.length > data[key].location.length) {
        data[key].location = rawLocation;
      }

      if (!data[key].carriers[carrier]) {
        data[key].carriers[carrier] = {
          carrier,
          carrierLogo,
          pallets: 0,
          grossWeight: 0
        };
      }

      data[key].totalPallets += pallets;
      data[key].totalGrossWeight += grossWeight;
      data[key].carriers[carrier].pallets += pallets;
      data[key].carriers[carrier].grossWeight += grossWeight;
    });

    const locations = Object.values(data)
      .map(loc => ({
        ...loc,
        carriers: Object.values(loc.carriers).sort(
          (a, b) => b.pallets - a.pallets
        )
      }))
      .sort((a, b) => b.totalPallets - a.totalPallets);

    const initialExpandedState = {};
    locations.forEach(loc => {
      initialExpandedState[loc.location] = true;
    });
    setExpandedLocations(initialExpandedState);

    return locations;
  }, [nonTlShipments]);

  const toggleLocation = useCallback(location => {
    setExpandedLocations(prev => ({
      ...prev,
      [location]: !prev[location]
    }));
  }, []);

  const formatNumber = useCallback(num => {
    return num?.toLocaleString() || '0';
  }, []);

  // Get color scheme for a location based on its index
  const getColorScheme = useCallback(index => {
    return cardColorSchemes[index % cardColorSchemes.length];
  }, []);

  return (
    <Box sx={{ p: 2, bgcolor: '#f8fafc' }}>
      <Grid container spacing={2}>
        {locationData.map((location, index) => {
          const colors = getColorScheme(index);

          return (
            <Grid item xs={12} md={6} lg={4} key={location.location}>
              <LocationCard>
                <LocationHeader
                  gradientstart={colors.gradientStart}
                  gradientend={colors.gradientEnd}
                >
                  <HeaderRow>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LocationOn fontSize='small' />
                      <Typography
                        variant='subtitle1'
                        sx={{ fontWeight: 'bold' }}
                      >
                        {location.location}
                        {location.state && `, ${location.state}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={`${formatNumber(location.totalPallets)} Pallets`}
                        size='small'
                        sx={{
                          bgcolor: colors.chipBg,
                          color: 'white',
                          fontWeight: 'bold',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      <IconButton
                        size='small'
                        onClick={() => toggleLocation(location.location)}
                        sx={{ color: 'white' }}
                      >
                        {expandedLocations[location.location] ? (
                          <ExpandLess />
                        ) : (
                          <ExpandMore />
                        )}
                      </IconButton>
                    </Box>
                  </HeaderRow>
                  <DateRow>
                    <CalendarToday sx={{ fontSize: '0.7rem' }} />
                    <Typography
                      variant='caption'
                      sx={{ fontSize: '0.7rem', fontWeight: 400 }}
                    >
                      {dateRangeLabel}
                    </Typography>
                  </DateRow>
                </LocationHeader>

                <CardContent sx={{ p: 0 }}>
                  <Collapse in={expandedLocations[location.location]}>
                    <Box sx={{ p: 1 }}>
                      <TableContainer component={Paper} variant='outlined'>
                        <Table size='small'>
                          <TableHead>
                            <TableRow sx={{ bgcolor: colors.light }}>
                              <StyledTableCell
                                sx={{ fontWeight: 600, color: colors.accent }}
                              >
                                Carrier
                              </StyledTableCell>
                              <StyledTableCell
                                align='right'
                                sx={{ fontWeight: 600, color: colors.accent }}
                              >
                                Pallets
                              </StyledTableCell>
                              <StyledTableCell
                                align='right'
                                sx={{ fontWeight: 600, color: colors.accent }}
                              >
                                Gross Weight (lbs)
                              </StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {location.carriers.map(carrier => (
                              <StyledTableRow key={carrier.carrier}>
                                <StyledTableCell>
                                  <CarrierCell>
                                    <Avatar
                                      src={
                                        carrier.carrierLogo
                                          ? base_logo_url + carrier.carrierLogo
                                          : ''
                                      }
                                      alt={carrier.carrier}
                                      sx={{
                                        width: 48,
                                        height: 48,
                                        border: '2px solid',
                                        borderColor: colors.light,
                                        boxShadow:
                                          '0 0 0 1px ' + colors.accent + '40',
                                        bgcolor: 'white',
                                        p: '2px',
                                        flexShrink: 0,
                                        '& img': { objectFit: 'contain' }
                                      }}
                                    >
                                      <LocalShipping fontSize='small' />
                                    </Avatar>
                                  </CarrierCell>
                                </StyledTableCell>
                                <StyledTableCell
                                  align='right'
                                  sx={{
                                    fontWeight: 'bold',
                                    color: colors.primary
                                  }}
                                >
                                  {formatNumber(carrier.pallets)}
                                </StyledTableCell>
                                <StyledTableCell
                                  align='right'
                                  sx={{ color: colors.accent }}
                                >
                                  {formatNumber(carrier.grossWeight)}
                                </StyledTableCell>
                              </StyledTableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Collapse>

                  {!expandedLocations[location.location] && (
                    <Box sx={{ p: 1.5 }}>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        gutterBottom
                      >
                        Top Carriers:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {location.carriers.slice(0, 5).map(carrier => (
                          <Chip
                            key={carrier.carrier}
                            size='small'
                            label={formatNumber(carrier.pallets)}
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: colors.light,
                              color: colors.accent,
                              '& .MuiChip-avatar': { width: 36, height: 36 }
                            }}
                            avatar={
                              <Avatar
                                src={
                                  carrier.carrierLogo
                                    ? base_logo_url + carrier.carrierLogo
                                    : ''
                                }
                                sx={{
                                  width: 36,
                                  height: 36,
                                  border: '2px solid',
                                  borderColor: colors.accent + '40',
                                  bgcolor: 'white',
                                  p: '1px',
                                  '& img': { objectFit: 'contain' }
                                }}
                              >
                                <LocalShipping fontSize='small' />
                              </Avatar>
                            }
                          />
                        ))}
                        {location.carriers.length > 5 && (
                          <Chip
                            size='small'
                            label={`+${location.carriers.length - 5} more`}
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: '#e2e8f0',
                              color: '#475569'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </LocationCard>
            </Grid>
          );
        })}

        {locationData.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color='text.secondary'>
                No location data available
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {locationData.length > 0 && (
        <Paper
          sx={{
            p: 2,
            mt: 2,
            bgcolor: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            Total Locations: {locationData.length}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Total Pallets:{' '}
            <span style={{ fontWeight: 'bold', color: '#2563eb' }}>
              {formatNumber(
                locationData.reduce((sum, loc) => sum + loc.totalPallets, 0)
              )}
            </span>
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            As of {dateRangeLabel}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PalletLocationBreakdown;
